import { GridColDef, GridValueGetterParams, GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import { BalanceHistory } from "../../../types/finance";
import { capitalizeFirstLetter } from "../../../utils/helper";

export const balanceHistoryColumns: GridColDef<BalanceHistory>[] = [
  {
    field: "created_at",
    headerName: "Time",
    minWidth: 200,
    sortingOrder: ["desc", "asc", null],
    sortable: true,
    filterable: true,
    valueGetter: (params: GridValueGetterParams<BalanceHistory>) =>
      params.row?.created_at
        ? dayjs(params.row.created_at).format("MMM DD, YYYY hh:mm A")
        : "",
    renderCell: (params: GridRenderCellParams<BalanceHistory>) => {
      if (!params.row?.created_at) return <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>-</Typography>;
      try {
        const formatted = dayjs(params.row.created_at).format("MMM DD, YYYY hh:mm A");
        return <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>{formatted}</Typography>;
      } catch (error) {
        return <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>{params.row.created_at}</Typography>;
      }
    },
  },
  {
    field: "tx_notes",
    headerName: "Description",
    flex: 0.6,
    minWidth: 200,
    maxWidth: 320,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams<BalanceHistory>) => (
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          fontSize: { xs: "0.75rem", sm: "0.875rem" },
          wordBreak: "break-word",
        }}
      >
        {capitalizeFirstLetter(
          params.row?.tx_notes || params.row?.notes || "N/A"
        )}
      </Typography>
    ),
    disableExport: true,
  },
  {
    field: "notes",
    headerName: "Live Report",
    flex: 0.55,
    minWidth: 200,
    maxWidth: 340,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams<BalanceHistory>) => (
      <Typography
        variant="body2"
        sx={{
          fontSize: { xs: "0.72rem", sm: "0.82rem" },
          color: "text.primary",
          wordBreak: "break-word",
        }}
      >
        {capitalizeFirstLetter(params.row?.notes || "N/A")}
      </Typography>
    ),
  },
  {
    field: "debit",
    headerName: "Debit",
    headerAlign: "center",
    type: "number",
    minWidth: 140,
    filterable: false,
    align: "right",
    valueGetter: (params: GridValueGetterParams<BalanceHistory>) => {
      const value = params.row?.debit;
      return value != null && value > 0 ? `$${Number(value).toFixed(2)}` : "-";
    },
    renderCell: (params: GridRenderCellParams<BalanceHistory>) => {
      const value = params.row?.debit;
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: value != null && value > 0 ? 600 : 400,
            color: value != null && value > 0 ? "error.main" : "text.secondary",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          }}
        >
          {value != null && value > 0 ? `$${Number(value).toFixed(2)}` : "-"}
        </Typography>
      );
    },
  },
  {
    field: "credit",
    headerName: "Credit",
    headerAlign: "center",
    type: "number",
    minWidth: 140,
    filterable: false,
    align: "right",
    valueGetter: (params: GridValueGetterParams<BalanceHistory>) => {
      const value = params.row?.credit;
      return value != null && value > 0 ? `$${Number(value).toFixed(2)}` : "-";
    },
    renderCell: (params: GridRenderCellParams<BalanceHistory>) => {
      const value = params.row?.credit;
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: value != null && value > 0 ? 600 : 400,
            color: value != null && value > 0 ? "success.main" : "text.secondary",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          }}
        >
          {value != null && value > 0 ? `$${Number(value).toFixed(2)}` : "-"}
        </Typography>
      );
    },
  },
  {
    field: "user_comment",
    headerName: "Comment",
    flex: 0.5,
    minWidth: 180,
    maxWidth: 300,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams<BalanceHistory>) => (
      <Typography
        variant="body2"
        sx={{
          fontSize: { xs: "0.72rem", sm: "0.8rem" },
          color: "text.secondary",
          fontStyle: params.row?.user_comment ? "italic" : "normal",
          wordBreak: "break-word",
        }}
      >
        {params.row?.user_comment || "-"}
      </Typography>
    ),
  },
];

